import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import { fetchPushNotificationRecords, fetchPushNotificationRecordById } from '../../../controllers/pushNotificationController';
import { fetchUserById } from '../../../controllers/userManagementController';
import BackButton from '../../../components/BackButton';
import { useNavigate } from 'react-router-dom';

const defaultPagination = {
  pageNumber: 1,
  pageSize: 10,
  totalCount: 0,
  totalPages: 1,
  hasNext: false,
  hasPrevious: false,
};

const getTargetTypeLabel = (targetType) => {
  switch (targetType) {
    case 0:
      return 'Tüm Üyeler';
    case 1:
      return 'Gruplar';
    case 2:
      return 'Üyeler';
    default:
      return 'Bilinmeyen';
  }
};

const PushNotificationRecordsTab = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [recordsMeta, setRecordsMeta] = useState(defaultPagination);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [recordsError, setRecordsError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [reloadKey, setReloadKey] = useState(0);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);
  const [detailSender, setDetailSender] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  const loadRecords = useCallback(
    async ({ pageNumber, pageSize }) => {
      setRecordsLoading(true);
      setRecordsError('');
      try {
        const { data, meta } = await fetchPushNotificationRecords({ pageNumber, pageSize });
        const recordsArray = Array.isArray(data) ? data : [];
        setRecords(recordsArray);
        setRecordsMeta({
          pageNumber: meta?.currentPage ?? pageNumber,
          pageSize: meta?.pageSize ?? pageSize,
          totalCount: meta?.totalCount ?? recordsArray.length,
          totalPages: meta?.totalPages ?? (Math.ceil((meta?.totalCount ?? 0) / (meta?.pageSize ?? pageSize)) || 1),
          hasNext: meta?.hasNext ?? false,
          hasPrevious: meta?.hasPrevious ?? false,
        });
      } catch (error) {
        setRecords([]);
        setRecordsMeta(defaultPagination);
        setRecordsError(error?.message || 'Gönderim kayıtları alınamadı');
      } finally {
        setRecordsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadRecords({ pageNumber: page + 1, pageSize: rowsPerPage });
  }, [page, rowsPerPage, reloadKey, loadRecords]);

  const handleRefresh = () => {
    loadRecords({ pageNumber: page + 1, pageSize: rowsPerPage });
  };

  const openDetail = async (record) => {
    if (!record) return;
    const recordId = record.id || record.Id;
    const senderId = record.sentById || record.SentById || record.sentBy || record.SentBy;

    setDetailRecord(record);
    setDetailSender(null);
    setDetailError('');
    setDetailLoading(true);
    setDetailOpen(true);
    try {
      const detail = await fetchPushNotificationRecordById(recordId);
      setDetailRecord(detail);
      if (senderId) {
        try {
          const sender = await fetchUserById(senderId);
          setDetailSender(sender);
        } catch (err) {
          // Silent fail for sender
        }
      }
    } catch (err) {
      setDetailError(err?.message || 'Detaylar alınamadı');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetailRecord(null);
    setDetailSender(null);
    setDetailError('');
  };

  const renderHistoryTable = () => {
    if (recordsLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      );
    }
    if (recordsError) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{recordsError}</Alert>
        </Box>
      );
    }
    if (!records.length) {
      return (
        <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
          Henüz gönderim kaydı bulunmuyor.
        </Box>
      );
    }

    const tableCellHeadSx = {
      fontSize: '14px',
      fontWeight: 600,
      borderBottom: '1px dashed #e8e8e8',
      px: 1.5,
      py: 1.5,
      color: '#444444',
    };

    const tableCellBodySx = (index) => ({
      borderBottom: index === records.length - 1 ? 'none' : '1px dashed #e8e8e8',
      px: 1.5,
      py: 1.5,
      color: '#444444',
      fontSize: '14px',
      fontWeight: 500,
    });

    return (
      <TableContainer sx={{ py: 1, px: 2.5 }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: 'transparent' }}>
            <TableRow>
              <TableCell sx={tableCellHeadSx}>Başlık</TableCell>
              <TableCell sx={tableCellHeadSx}>Hedef</TableCell>
              <TableCell sx={tableCellHeadSx}>Alıcı Sayısı</TableCell>
              <TableCell sx={tableCellHeadSx}>Başarılı</TableCell>
              <TableCell sx={tableCellHeadSx}>Başarısız</TableCell>
              <TableCell sx={tableCellHeadSx}>Gönderim</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((record, index) => {
              const key = record.id || record.Id;
              const title = record.title || record.Title || '—';
              const targetType = record.targetType ?? record.TargetType ?? 0;
              const recipientCount = record.recipientCount ?? record.RecipientCount ?? 0;
              const successCount = record.successCount ?? record.SuccessCount ?? 0;
              const failureCount = record.failureCount ?? record.FailureCount ?? 0;
              const sentAt = record.sentAt || record.SentAt || record.createdAt || record.CreatedAt;

              const sentLabel = sentAt ? new Date(sentAt).toLocaleString('tr-TR') : '—';
              const successRate = recipientCount > 0 ? ((successCount / recipientCount) * 100).toFixed(1) : 0;

              return (
                <TableRow
                  key={key}
                  hover
                  onClick={() => openDetail(record)}
                  sx={{
                    cursor: 'pointer',
                    '& .MuiTableCell-root': tableCellBodySx(index),
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    },
                  }}
                >
                  <TableCell>
                    <Stack spacing={0.5}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#444444' }}>{title}</Typography>
                      {record.body && (
                        <Typography variant="caption" sx={{ fontSize: '13px', color: '#666666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300 }}>
                          {record.body}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip label={getTargetTypeLabel(targetType)} size="small" sx={{ fontSize: '12px', backgroundColor: '#f5f5f5', color: '#666666' }} />
                  </TableCell>
                  <TableCell>{recipientCount}</TableCell>
                  <TableCell>
                    <Chip label={successCount} size="small" sx={{ fontSize: '12px', backgroundColor: '#E7EDF4', color: '#0E4D94' }} />
                  </TableCell>
                  <TableCell>
                    <Chip label={failureCount} size="small" sx={{ fontSize: '12px', backgroundColor: '#ffebee', color: '#c62828' }} />
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.5}>
                      <Typography variant="body2" sx={{ fontSize: '14px', color: '#444444' }}>{sentLabel}</Typography>
                      {recipientCount > 0 && (
                        <Typography variant="caption" sx={{ fontSize: '13px', color: '#666666' }}>
                          Başarı: %{successRate}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box>
      <Stack spacing={2} sx={{ mb: 2 }}>
        <BackButton onClick={() => navigate('/notifications/app-notifications')} />
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Gönderim Kayıtları</Typography>
        <Button
          onClick={handleRefresh}
          disabled={recordsLoading}
          startIcon={recordsLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
          sx={{
            textTransform: 'none',
            fontSize: '13px',
            color: '#666666',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          Yenile
        </Button>
      </Stack>
      <Paper
        sx={{
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
          border: '1px solid #f5f5f5',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        {renderHistoryTable()}
        <Divider />
        <TablePagination
          component="div"
          count={recordsMeta.totalCount}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50]}
          sx={{
            '& .MuiTablePagination-toolbar': {
              px: 2,
            },
          }}
        />
      </Paper>

      <PushNotificationDetailDialog
        open={detailOpen}
        onClose={closeDetail}
        record={detailRecord}
        sender={detailSender}
        loading={detailLoading}
        error={detailError}
      />
    </Box>
  );
};

const PushNotificationDetailDialog = ({ open, onClose, record, sender, loading, error }) => {
  if (!open || !record) return null;

  const title = record.title || record.Title || '—';
  const body = record.body || record.Body || '';
  const targetType = record.targetType ?? record.TargetType ?? 0;
  const targetData = record.targetData || record.TargetData || [];
  const recipientCount = record.recipientCount ?? record.RecipientCount ?? 0;
  const successCount = record.successCount ?? record.SuccessCount ?? 0;
  const failureCount = record.failureCount ?? record.FailureCount ?? 0;
  const sentAt = record.sentAt || record.SentAt || record.createdAt || record.CreatedAt;
  const source = record.source || record.Source || null;
  const sourceId = record.sourceId || record.SourceId || null;
  const data = record.data || record.Data || null;
  const errors = record.errors || record.Errors || null;
  const deviceTypes = record.deviceTypes || record.DeviceTypes || null;

  const formatData = (dataObj) => {
    if (!dataObj || typeof dataObj !== 'object') return null;
    return Object.entries(dataObj).map(([key, value]) => ({ key, value: String(value) }));
  };

  const dataEntries = formatData(data);

  const sectionLabelSx = { fontSize: '14px', fontWeight: 600, color: '#444444', mb: 0.5 };
  const valueSx = { fontSize: '14px', color: '#444444' };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          fontSize: '18px',
          fontWeight: 500,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pr: 3,
        }}
      >
        Push Notification Detayı
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ py: 2, px: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} sx={{ color: '#0E4D94' }} />
          </Box>
        ) : (
          <Stack spacing={2.5}>
            {error && <Alert severity="error" sx={{ fontSize: '13px' }}>{error}</Alert>}

            <Box>
              <Typography variant="body2" sx={sectionLabelSx}>Başlık</Typography>
              <Typography sx={valueSx}>{title}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" sx={sectionLabelSx}>İçerik</Typography>
              <Typography variant="body2" sx={{ ...valueSx, whiteSpace: 'pre-wrap' }}>{body || '—'}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" sx={sectionLabelSx}>Hedef Tip</Typography>
              <Chip label={getTargetTypeLabel(targetType)} size="small" sx={{ backgroundColor: '#f5f5f5', color: '#666666', fontSize: '12px' }} />
            </Box>

            {targetData && targetData.length > 0 && (
              <Box>
                <Typography variant="body2" sx={sectionLabelSx}>Hedef Verileri</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {targetData.map((item, idx) => (
                    <Chip key={idx} label={item} size="small" variant="outlined" sx={{ fontSize: '12px', borderColor: '#e6e6e6', color: '#444444' }} />
                  ))}
                </Stack>
              </Box>
            )}

            <Box>
              <Typography variant="body2" sx={sectionLabelSx}>İstatistikler</Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <Typography variant="body2" sx={valueSx}>
                  <Box component="span" sx={{ fontWeight: 600 }}>Toplam Alıcı:</Box> {recipientCount}
                </Typography>
                <Typography variant="body2" sx={{ ...valueSx, color: '#0E4D94' }}>
                  <Box component="span" sx={{ fontWeight: 600 }}>Başarılı:</Box> {successCount}
                </Typography>
                <Typography variant="body2" sx={{ ...valueSx, color: failureCount > 0 ? '#b71c1c' : '#666666', fontWeight: failureCount > 0 ? 600 : 400 }}>
                  <Box component="span" sx={{ fontWeight: 600 }}>Başarısız:</Box> {failureCount}
                </Typography>
                {recipientCount > 0 && (
                  <Typography variant="body2" sx={{ ...valueSx, color: '#666666' }}>
                    <Box component="span" sx={{ fontWeight: 600 }}>Başarı Oranı:</Box> %{((successCount / recipientCount) * 100).toFixed(1)}
                  </Typography>
                )}
              </Stack>
            </Box>

            {deviceTypes && deviceTypes.length > 0 && (
              <Box>
                <Typography variant="body2" sx={sectionLabelSx}>Cihaz Tipleri</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {deviceTypes.map((type, idx) => (
                    <Chip key={idx} label={type === 0 ? 'iOS' : 'Android'} size="small" variant="outlined" sx={{ fontSize: '12px', borderColor: '#e6e6e6', color: '#444444' }} />
                  ))}
                </Stack>
              </Box>
            )}

            <Box>
              <Typography variant="body2" sx={sectionLabelSx}>Gönderim Bilgileri</Typography>
              <Stack spacing={0.25}>
                <Typography variant="body2" sx={valueSx}>
                  <Box component="span" sx={{ fontWeight: 600 }}>Gönderim Tarihi:</Box> {sentAt ? new Date(sentAt).toLocaleString('tr-TR') : '—'}
                </Typography>
                {source && (
                  <Typography variant="body2" sx={valueSx}>
                    <Box component="span" sx={{ fontWeight: 600 }}>Kaynak:</Box> {source}{sourceId ? ` (${sourceId})` : ''}
                  </Typography>
                )}
              </Stack>
            </Box>

            <Box>
              <Typography variant="body2" sx={sectionLabelSx}>Gönderen</Typography>
              {record.sentById ? (
                sender ? (
                  <Stack spacing={0.25}>
                    <Typography variant="body2" sx={valueSx}>
                      <Box component="span" sx={{ fontWeight: 600 }}>Kullanıcı Adı:</Box> {sender.username || sender.fullName || '—'}
                    </Typography>
                    {sender.email && (
                      <Typography variant="body2" sx={{ ...valueSx, color: '#666666' }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>E-posta:</Box> {sender.email}
                      </Typography>
                    )}
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ color: '#666666' }}>Kullanıcı bilgisi yükleniyor...</Typography>
                )
              ) : (
                <Typography variant="body2" sx={{ color: '#666666' }}>Gönderen bilgisi mevcut değil.</Typography>
              )}
            </Box>

            {dataEntries && dataEntries.length > 0 && (
              <Box>
                <Typography variant="body2" sx={sectionLabelSx}>Ek Veri (Data)</Typography>
                <Stack spacing={0.25}>
                  {dataEntries.map(({ key, value }) => (
                    <Typography key={key} variant="body2" sx={valueSx}>
                      <Box component="span" sx={{ fontWeight: 600 }}>{key}:</Box> {value}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}

            {errors && Array.isArray(errors) && errors.length > 0 && (
              <Box>
                <Typography variant="body2" sx={sectionLabelSx}>Hatalar</Typography>
                <Box
                  component="ul"
                  sx={{
                    m: 0,
                    pl: 2,
                    maxHeight: 200,
                    overflow: 'auto',
                    backgroundColor: '#ffcdd2',
                    p: 1.5,
                    borderRadius: '5px',
                    border: '1px solid #ef9a9a',
                  }}
                >
                  {errors.map((err, idx) => (
                    <li key={idx}>
                      <Typography variant="body2" sx={{ fontSize: '13px', color: '#b71c1c', fontWeight: 500 }}>{err}</Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #f0f0f0' }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            textTransform: 'none',
            borderRadius: '5px',
            backgroundColor: '#0E4D94',
            boxShadow: 'none',
            '&:hover': { backgroundColor: '#0a3a70', boxShadow: 'none' },
          }}
        >
          Kapat
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PushNotificationRecordsTab;

